import { logbookApiService } from '../services/logbookApi';
import type { CaseChoice } from '../components/logbook/CaseChooser';
import type { LogbookCaseItemType } from '../types/logbook';

/** Enough of a case to name it back to the operator once the link is made. */
export interface LinkedCase {
  id: string;
  reference: string;
  title: string;
}

/**
 * Attaches a note, warning or ban to the case it belongs to, opening the case first when the
 * operator chose a new one.
 *
 * This runs after the record exists, never before: a case is only worth opening once there is
 * something to put in it, and a ban form that is abandoned half-written should leave nothing behind.
 * The caller therefore treats a failure here as a failed link and not as a failed ban — the ban is
 * already issued by the time this is reached, and telling somebody their ban failed because a case
 * could not be written would be a lie they would act on.
 */
export async function linkRecordToCase(
  choice: CaseChoice,
  itemType: LogbookCaseItemType,
  itemId: string,
  /** Named on a newly opened case, so the case knows who it is about from the start. */
  subjectUuid?: string,
): Promise<LinkedCase> {
  const target: LinkedCase = choice.mode === 'new'
    ? await logbookApiService.createCase({
      title: choice.title.trim(),
      subjects: subjectUuid ? [{ uuid: subjectUuid, role: 'suspect' }] : undefined,
    })
    : choice.entry;

  await logbookApiService.linkCase(target.id, itemType, itemId);
  return target;
}
